<template>
    <BModal
        class="signin-modal"
        id="signInModal"
        ref="signInModal"
        v-model="showSignIn"
        size="md"
        dialog-class="signin-modal__dialog"
        content-class="signin-modal__content"
        header-class="signin-modal__header"
        body-class="signin-modal__body"
        :hide-header-close="true"
        :no-close-on-backdrop="false"
        teleport-to="body"
    >
        <template #header>
            <h5 class="modal-title">
                <span :class="[currentLanguage === 'AR' ? 'rtlText' : '']">{{ $t('signin') }}</span>
            </h5>
            <button
                type="button"
                class="modal-close"
                aria-label="Close"
                @click="closeSignInModal"
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                >
                    <path
                        d="M11.033,88l4.549-4.549a1.43,1.43,0,0,0,0-2.022L14.57,80.419a1.43,1.43,0,0,0-2.022,0L8,84.967,3.451,80.419a1.43,1.43,0,0,0-2.022,0L.419,81.43a1.43,1.43,0,0,0,0,2.022L4.967,88,.419,92.549a1.43,1.43,0,0,0,0,2.022L1.43,95.581a1.43,1.43,0,0,0,2.022,0L8,91.033l4.549,4.549a1.43,1.43,0,0,0,2.022,0l1.011-1.011a1.43,1.43,0,0,0,0-2.022Z"
                        transform="translate(0 -80)"
                        fill="#878787"
                    />
                </svg>
            </button>
        </template>

        <template #default>
            <div class="signin-tabs" v-if="phoneLoginAllowed">
                <button type="button" class="signin-tab" :class="{ active: loginMode === 'code' }" @click="switchMode('code')">
                    {{ $t('signin-modal.tab-code') }}
                </button>
                <button type="button" class="signin-tab" :class="{ active: loginMode === 'phone' }" @click="switchMode('phone')">
                    {{ $t('signin-modal.tab-phone') }}
                </button>
            </div>

            <div v-if="errors.length" class="signin-errors text-center">
                <span v-for="error in errors" :key="error.id" class="text-danger">
                    {{ error.message }}
                </span>
            </div>

            <form v-if="loginMode === 'code' || !phoneLoginAllowed" @submit.prevent="submitAccessCode" class="text-center">
                <label for="accessCode" :class="[currentLanguage === 'AR' ? 'rtlText' : '']">
                    {{ $t('signin.title') }}
                </label>

                <input
                    id="accessCode"
                    v-model="accessCode"
                    type="tel"
                    name="access-code"
                    class="form-control form-control-lg text-center signin-input"
                    :placeholder="$t('input.accesscode')"
                    :pattern="[currentCountry === 'CB' ? '[A-Z0-9]*' : '[0-9]*']"
                    :inputmode="[currentCountry === 'CB' ? 'text': 'numeric']"
                    :class="[currentLanguage === 'AR' ? 'rtlText' : '']"
                />

                <div class="form-button">
                    <button type="submit"
                            class="btn btn-danger btn-lg rounded-pill d-inline-flex align-items-center signin-btn">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20.015"
                            height="15.325"
                            viewBox="0 0 20.015 15.325"
                        >
                            <path
                                d="M16.262,79.011H12.978a.47.47,0,0,1-.469-.469V76.978a.47.47,0,0,1,.469-.469h3.284a1.25,1.25,0,0,0,1.251-1.251V67.753A1.25,1.25,0,0,0,16.262,66.5H12.978a.47.47,0,0,1-.469-.469V64.469A.47.47,0,0,1,12.978,64h3.284a3.754,3.754,0,0,1,3.753,3.753v7.506A3.754,3.754,0,0,1,16.262,79.011Zm-1.837-7.857L7.857,64.586a.94.94,0,0,0-1.6.665V69H.938A.936.936,0,0,0,0,69.942v3.753a.936.936,0,0,0,.938.938H6.255v3.753a.94.94,0,0,0,1.6.665l6.567-6.567A.946.946,0,0,0,14.425,71.154Z"
                                transform="translate(0 -64)"
                            />
                        </svg>
                        <span :class="[currentLanguage === 'AR' ? 'rtlText' : '']">{{ $t('signin') }}</span>
                    </button>
                </div>
            </form>

            <form v-if="loginMode === 'phone' && phoneLoginAllowed" @submit.prevent="submitPhoneLogin" class="text-center">
                <label for="phoneLogin" :class="[currentLanguage === 'AR' ? 'rtlText' : '']">
                    {{ $t('signin-modal.phone-description') }}
                </label>

                <input
                    id="phoneLogin"
                    v-model="phoneNumber"
                    type="tel"
                    name="phone-login"
                    class="form-control form-control-lg text-center signin-input"
                    :placeholder="phoneFormat"
                    :maxlength="phoneFormat.length"
                    inputmode="numeric"
                    @input="phoneNumber = phoneNumber.replace(/[^0-9]/g, '')"
                />

                <div class="form-button">
                    <button type="submit"
                            class="btn btn-danger btn-lg rounded-pill d-inline-flex align-items-center signin-btn">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20.015"
                            height="15.325"
                            viewBox="0 0 20.015 15.325"
                        >
                            <path
                                d="M16.262,79.011H12.978a.47.47,0,0,1-.469-.469V76.978a.47.47,0,0,1,.469-.469h3.284a1.25,1.25,0,0,0,1.251-1.251V67.753A1.25,1.25,0,0,0,16.262,66.5H12.978a.47.47,0,0,1-.469-.469V64.469A.47.47,0,0,1,12.978,64h3.284a3.754,3.754,0,0,1,3.753,3.753v7.506A3.754,3.754,0,0,1,16.262,79.011Zm-1.837-7.857L7.857,64.586a.94.94,0,0,0-1.6.665V69H.938A.936.936,0,0,0,0,69.942v3.753a.936.936,0,0,0,.938.938H6.255v3.753a.94.94,0,0,0,1.6.665l6.567-6.567A.946.946,0,0,0,14.425,71.154Z"
                                transform="translate(0 -64)"
                            />
                        </svg>
                        <span :class="[currentLanguage === 'AR' ? 'rtlText' : '']">{{ $t('signin') }}</span>
                    </button>
                </div>
            </form>
        </template>
        <template
            #footer
            v-if="
        (
          this.currentCountry != 'UK' &&
          this.currentCountry != 'LT' &&
          this.currentCountry != 'EE' &&
          !(
            this.currentCountry == 'CZ' &&
            helpers.portal_name == 'Fizzzplay'
          )
        ) ||
        (
          this.currentCountry == 'LT' &&
          (
            helpers.portal_name == 'Fizzzplay' ||
            helpers.portal_name == 'Vemoplay' ||
            helpers.portal_name == 'Runsio' ||
            helpers.portal_name == 'Fitlandclub'
          )
        )
      "
        >
            <div class="registration-text">
                <h4 :class="[currentLanguage == 'AR' ? 'rtlText': '']">{{
                        $t("register.headline")
                    }}</h4>
                <a :href="landingPage" class="registration_link"
                   :class="[currentLanguage == 'AR' ? 'rtlText': '']">&gt;&gt;
                    {{ $t("register.link") }} &lt;&lt;</a>
            </div>
        </template>
    </BModal>
</template>


<script>
import axios from 'axios'
import EventBus from '../helper/event_bus'
import {helpers} from '../helper/portal_config'
import {BModal} from "bootstrap-vue-next";

export default {
    name: 'SignInModal',
    components: {BModal},
    data() {
        return {
            showSignIn: false,
            accessCode: '',
            phoneNumber: '',
            loginMode: 'code',
            phoneLoginAllowed: false,
            phoneFormat: '',
            link: '',
            errors: [],
            helpers: '',
            currentLanguage: '',
            currentCountry: ''
        }
    },
    computed: {
        landingPage() {
            const country = this.currentCountry || 'UK'
            return helpers.landingUrl[0][country]
        }
    },
    methods: {
        closeSignInModal() {
            this.errors = []
            this.showSignIn = false
        },
        switchMode(mode) {
            this.loginMode = mode
            this.errors = []
        },
        setCurrentCountry() {
            const {host} = window.location
            this.currentCountry = host.split('.')[0].slice(0, 2).toUpperCase()
        },
        setCurrentLanguage() {
            const {host} = window.location
            const country = host.split('.')[0].slice(0, 2).toUpperCase()
            let lang = country
            if (helpers.countryDefaultLanguage.hasOwnProperty(country)) {
                lang = helpers.countryDefaultLanguage[country]
            }
            this.currentLanguage = (lang || 'EN').toUpperCase()
        },
        setPhoneFormat() {
            if (helpers.languagePhoneFormat && helpers.languagePhoneFormat[this.currentCountry]) {
                this.phoneFormat = helpers.languagePhoneFormat[this.currentCountry]
            } else {
                this.phoneFormat = 'xxxxxxxxxx'
            }
        },
        checkPhoneLoginAllowed() {
            axios
                .get(`${helpers.API_BASE_URL}/store/checkPhoneLoginAllowed?portal_name=${helpers.portal_name}&country=${this.currentCountry}`)
                .then((response) => {
                    if (response.data.treatment === 1) {
                        this.phoneLoginAllowed = response.data.allow_phone_login === true
                    }
                })
                .catch(() => {
                    this.phoneLoginAllowed = false
                })
        },
        checkForm() {
            this.errors = []
            if (this.accessCode && this.accessCode.length === 6) return true
            const errorObj = {id: 1, message: this.$i18n.t('signin.error')}
            this.errors.push(errorObj)
            return false
        },
        getMessageForErrorCode(errorCode) {
            this.errors = []
            const langErrorMsg = this.$i18n.t(`error.E${errorCode}`)
            this.errorMsg = langErrorMsg || this.$i18n.t('error.default')
            this.errors.push({id: 222, message: this.errorMsg})
        },
        submitAccessCode() {
            if (!this.checkForm()) {
              return
            }
            axios
                .get(
                    `${helpers.API_BASE_URL}/store/checkAccessCode?portal_name=${helpers.portal_name}&country=${this.currentCountry}&language=${this.currentLanguage}&access_code=${this.accessCode}`
                )
                .then((response) => {
                    if (response.data.treatment === 1) {
                        this.$store
                            .dispatch('globals/login', {accessCode: this.accessCode})
                            .then(() => {
                                this.closeSignInModal()
                                if (this.link) this.$router.push(this.link)
                            })
                    }
                })
                .catch((error) => {
                    this.getMessageForErrorCode(error?.response?.data?.error_code)
                })
        },
        submitPhoneLogin() {
            this.errors = []
            if (!this.phoneNumber || this.phoneNumber.length !== this.phoneFormat.length) {
                const errorObj = { id: 1, message: this.$i18n.t('signin-modal.phone-error') }
                this.errors.push(errorObj)
                return false
            }

            axios
                .get(
                    `${helpers.API_BASE_URL}/store/loginWithPhone?portal_name=${helpers.portal_name}&country=${this.currentCountry}&language=${this.currentLanguage}&phone_number=${this.phoneNumber}`
                )
                .then((response) => {
                    if (response.data.treatment === 1) {
                        this.$store
                            .dispatch('globals/login', {accessCode: response.data.access_code})
                            .then(() => {
                                this.closeSignInModal()
                                if (this.link) this.$router.push(this.link)
                            })
                    }
                })
                .catch((error) => {
                    this.getMessageForErrorCode(error?.response?.data?.error_code)
                })
        },
        onShowSignIn(link) {
            this.link = link || ''
            this.$nextTick(() => {
                this.showSignIn = true
            })
        }
    },
    created() {
        this.helpers = helpers
        this.setCurrentLanguage()
        this.setCurrentCountry()
        this.setPhoneFormat()
        this.checkPhoneLoginAllowed()
    },
    mounted() {
        EventBus.$on('SHOW_SIGNIN_MODAL', this.onShowSignIn)
    },
    beforeDestroy() {
        EventBus.$off('SHOW_SIGNIN_MODAL', this.onShowSignIn)
    },
}
</script>

<style scoped>
.signin-modal__header .modal-title {
    font-weight: 800;
    font-size: 34px;
    line-height: 1.2;
}

.signin-modal__header .btn-close {
    top: 18px;
    right: 18px;
}

.signin-input {
    border: 3px solid #e8e8e8;
}

.form-button {
    padding: 20px 0;
}

:global(.modal-backdrop.show) {
    background-color: #fff;
    opacity: .7;
}

@media (max-width: 576px) {
    .signin-modal__dialog {
        max-width: 92vw;
    }

    .signin-modal__header .modal-title {
        font-size: 28px;
    }
}

.registration-text {
    text-align: center;
    margin-top: 12px;
    margin-bottom: 8px;

    .registration_link {
        font-weight: bold;
    }
}

.signin-tabs {
    display: flex;
    margin-bottom: 20px;
    border-bottom: 2px solid #ddd;
}

.signin-tab {
    flex: 1;
    padding: 10px 5px;
    text-align: center;
    background: transparent;
    border: none;
    cursor: pointer;
    font-size: 14px;
    font-weight: 600;
    color: #666;
    border-bottom: 2px solid transparent;
    margin-bottom: -2px;
    transition: all 0.2s;
}

.signin-tab.active {
    color: #c50000;
    border-bottom-color: #c50000;
}

.signin-tab:hover {
    color: #c50000;
}

.signin-errors {
    margin: 10px 0;
}
</style>
